import { useCallback, useEffect, useMemo, useState } from "react"
import { fetchOpportunities, IFetchOpportunitiesFilters } from "src/services/api/opportunities"
import { IOpportunity } from "src/services/api/opportunities/types"
import { useModalStore } from "src/store/modalStore"
import { useOpportunitiesStore } from "src/store"
import { useDrawerStore } from "src/store/drawerStore"
import { useMessageStore } from "src/store/messageStore"
import { OpportunitiesState } from "src/store/opportunitiesStore/types"
import { useSnackbarStore } from "src/store/snackbarStore"

export const useOpportunitiesSection = (filters?: IFetchOpportunitiesFilters) => {
    const [objectives, setObjectives] = useState<IOpportunity[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [showAll, setShowAll] = useState<boolean>(false)
    const showSnackbar = useSnackbarStore((s) => s.showSnackbar)


    const setOpenModal = useModalStore((s: any) => s.setOpen)
    const setOpportunitiesData = useOpportunitiesStore((s: any) => s.setOpportunitiesData)
    const opportunitiesData = useOpportunitiesStore((s: OpportunitiesState) => s.opportunitiesData)
    const refreshKey = useOpportunitiesStore((s: OpportunitiesState) => s.refreshKey);
    const actualContext = useMessageStore((s: any) => s.actualContext)
    const setJsonContext = useMessageStore((s: any) => s.setJsonContext)
    const setActualContext = useMessageStore((s: any) => s.setActualContext)
    const openDrawer = useDrawerStore((s: any) => s.openDrawer)

    const handleOpenNewObjectiveModal = () => {
        setOpenModal("createOpportunitiesModal")
    }
    
    const loadOpportunities = useCallback(async () => {
        try {
            setIsLoading(true)
            setError(null)
            const filtersToUse: IFetchOpportunitiesFilters = {
                ...filters,
                include_movement_rollups: true,
                include_expired: true,
            }
            const response = await fetchOpportunities(filtersToUse)
            setOpportunitiesData(response)
            setObjectives(response.objectives ?? [])

            if (response.objectives?.length) {
                const warn = response.objectives.flatMap((o) => o.warnings ?? [])
                if (warn.length) {
                    const text = Array.from(new Set(warn)).slice(0, 8).join(" · ")
                    showSnackbar(text, "warning")
                }
            }
        } catch (err) {
            setError("Não foi possível carregar as oportunidades.")
        } finally {
            setIsLoading(false)
        }
    }, [filters, setOpportunitiesData, showSnackbar])
    
    // Serializar filters para usar como dependência estável
    const filtersKey = useMemo(() => JSON.stringify(filters || {}), [filters])


    useEffect(() => {
        loadOpportunities()
    }, [refreshKey, filtersKey, loadOpportunities])

    const formatAddedAt = (createdDate: string) => {
        const date = new Date(createdDate)
        if (Number.isNaN(date.getTime())) {
            return createdDate
        }

        return date.toLocaleDateString("pt-BR")
    }

    const formatDueDate = (objective: IOpportunity) => {
        const due = objective.dueDate?.trim()
        if (due) {
            const parsed = new Date(`${due}T12:00:00`)
            if (!Number.isNaN(parsed.getTime())) {
                return parsed.toLocaleDateString("pt-BR")
            }
            return due
        }
        return ""
    }

    const activeObjectives = useMemo(
        () => objectives.filter((o) => !o.isExpired),
        [objectives],
    )
    const expiredObjectives = useMemo(
        () => objectives.filter((o) => o.isExpired),
        [objectives],
    )

    const visibleObjectives = showAll ? objectives : objectives.slice(0, 4)

    const handleClickAIButton = async () => {
        if (actualContext !== 'opportunities') {
            try {
                setActualContext('opportunities')
                setJsonContext(opportunitiesData)
            } catch (error) {
                console.error("Error setting opportunities context:", error)
            }
        }
        openDrawer()
    }

    const toggleShowAll = () => {
        setShowAll((prev) => !prev)
    }

    return {
        objectives,
        activeObjectives,
        expiredObjectives,
        isLoading,
        setIsLoading,
        error,
        showAll,
        visibleObjectives,
        handleOpenNewObjectiveModal,
        formatAddedAt,
        formatDueDate,
        toggleShowAll,
        handleClickAIButton,
        loadOpportunities,
    }
}


